// AiOutputNotice — the persistent "deemed accurate but not guaranteed" posture that sits
// with every AI output in the app.
//
// Laura, beta feedback item 6. ES ruled this "THE SECOND READING": not the line inside the
// training module (that shipped in mig 221), but a standing disclaimer wherever the AI
// actually speaks. Training is read once at onboarding; the output is read every day, and
// the moment that matters is the one where an agent is about to act on something a model
// wrote.
//
// THE WORDS ARE THE TRAINING'S WORDS, VERBATIM. `training_modules` says "All information it
// surfaces is deemed accurate but not guaranteed" and "Treat what it returns as a starting
// point for your own due diligence, never as a finding." If this component paraphrased,
// the brokerage would be making two subtly different claims about the same system — and
// the one an agent sees daily would be the one that is not in the compliance record.
// `tests/unit/ai-output-notice.test.mjs` pins the phrase against the module.
//
// NOT DISMISSIBLE, and there is no "don't show again". A disclaimer an agent can turn off
// is a disclaimer that is absent exactly when it matters; the point is that it is on the
// screen next to the words the client will read.
//
// TONE: this is not a warning banner. The AI being fallible is the normal condition, not an
// error state, so it renders as quiet supporting text (destructive/amber styling would cry
// wolf on every screen and get tuned out within a week). `role="note"` carries the meaning
// to assistive tech without the alarm.
import { Sparkles } from 'lucide-react'

/** The compliance phrase. Verbatim from the training module — do not reword. */
export const AI_NOT_GUARANTEED = 'deemed accurate but not guaranteed'

interface AiOutputNoticeProps {
  /**
   * What the AI produced here, in the client's words — "This answer", "This draft",
   * "These dates". It leads the sentence, so it must name the thing on screen rather
   * than the feature that made it.
   */
  subject?: string
  /**
   * `send` when the output is about to leave the building as a client communication —
   * adds the read-it-first instruction the training makes the agent responsible for.
   * `rely` (default) when it is something they will act on but not forward.
   */
  action?: 'rely' | 'send'
  className?: string
}

export function AiOutputNotice({ subject = 'This', action = 'rely', className = '' }: AiOutputNoticeProps) {
  return (
    <p
      role="note"
      data-ai-output-notice
      className={`flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground ${className}`}
    >
      <Sparkles aria-hidden className="mt-0.5 h-3 w-3 shrink-0" />
      <span>
        {subject} is AI-generated and {AI_NOT_GUARANTEED}.{' '}
        {action === 'send'
          ? 'Read it in full and correct anything the documents don’t support — it goes out under your name.'
          : 'Treat it as a starting point for your own checks, never as a finding.'}
      </span>
    </p>
  )
}
